import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { normalizeSearch } from '@pos/core';
import { computeBalances, rejectedAccountEntryIds, type AccountEntryRow, type CustomerRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { supabase } from '../../lib/supabase.ts';

/** Clientes vivos del negocio, por nombre. undefined mientras carga. */
export function useCustomers(): CustomerRow[] | undefined {
  const { business } = useSession();
  const rows = useLiveQuery(() => db.customers.where('business_id').equals(business.id).toArray(), [business.id]);
  return useMemo(() => rows?.filter((c) => !c.deleted_at).sort((a, b) => a.name.localeCompare(b.name, 'es')), [rows]);
}

/** Saldo de cuenta corriente por cliente (solo si el usuario puede verlo; si no, vacío). */
export function useBalances(): Map<string, number> | undefined {
  const { business } = useSession();
  return useLiveQuery(async () => {
    const [balances, entries, rejected] = await Promise.all([
      db.customer_balances.where('business_id').equals(business.id).toArray(),
      db.account_entries.where('business_id').equals(business.id).toArray(),
      rejectedAccountEntryIds(db),
    ]);
    return computeBalances(balances, entries, rejected);
  }, [business.id]);
}

export function customerSearchText(c: CustomerRow): string {
  return normalizeSearch(`${c.name} ${c.phone ?? ''} ${c.doc_number ?? ''} ${c.email ?? ''}`);
}

export function filterCustomers(customers: CustomerRow[], query: string): CustomerRow[] {
  const terms = normalizeSearch(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return customers;
  return customers.filter((c) => {
    const text = customerSearchText(c);
    return terms.every((t) => text.includes(t));
  });
}

/**
 * Movimientos de cuenta corriente de un cliente: los de este dispositivo más los del servidor
 * (si hay conexión). Más nuevos primero.
 */
export async function fetchStatement(customerId: string): Promise<{ entries: AccountEntryRow[]; online: boolean }> {
  const local = await db.account_entries.where('customer_id').equals(customerId).toArray();
  const byId = new Map(local.map((e) => [e.id, e]));
  let online = false;
  if (navigator.onLine) {
    const { data, error } = await supabase
      .from('account_entries')
      .select('*')
      .eq('customer_id', customerId)
      .order('occurred_at', { ascending: false })
      .limit(300);
    if (!error && data) {
      online = true;
      for (const r of data as AccountEntryRow[]) byId.set(r.id, { ...r, amount: Number(r.amount), server_xid: String(r.server_xid) });
    }
  }
  return { entries: [...byId.values()].sort((a, b) => b.occurred_at.localeCompare(a.occurred_at)), online };
}
